import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RegisterFeedback, PageHeader, EmptyState } from "./_shared";
import { Clock, CalendarDays, ShieldAlert, Truck, ArrowUpRight, Check, X as XIcon } from "lucide-react";

const ICON = {
  timesheet_pending: Clock,
  timesheet_reviewed: Clock,
  leave_pending: CalendarDays,
  leave_reviewed: CalendarDays,
  safety_alert: ShieldAlert,
  damage_report: Truck,
};

const SEV_COLOR = {
  critical: "border-red-600 bg-red-50 dark:bg-red-500/10",
  warning: "border-orange-500 bg-orange-50 dark:bg-orange-500/10",
  info: "border-blue-500 bg-blue-50 dark:bg-blue-500/10",
  success: "border-green-500 bg-green-50 dark:bg-green-500/10",
};

export default function Inbox() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const isOffice = user.role !== "field";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const load = async () => {
    setLoading(true); setLoadError(false);
    try { const { data } = await api.get("/notifications"); setItems(data); }
    catch (error) { setLoadError(true); console.warn("Alerts request failed", { status: error.response?.status }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="inbox-page" className="p-6 lg:p-10 max-w-5xl mx-auto">
      <PageHeader
        tag="/ Inbox · Digest"
        title={isOffice ? "Notifications" : "Alerts"}
        subtitle={isOffice
          ? "Everything that needs your attention — across timesheets, leave, safety and fleet."
          : "Updates on your approvals, requests and submissions over the last 14 days."}
      />
      {loading || loadError ? <RegisterFeedback loading={loading} error={loadError} onRetry={load} label="alerts" /> : items.length === 0 ? (
        <EmptyState title="All caught up" description="Nothing new in your inbox." />
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const Icon = ICON[n.type] || ShieldAlert;
            const isCritical = n.severity === "critical";
            return (
              <Link
                to={n.link}
                key={n.id}
                data-testid={`inbox-item-${n.id}`}
                className={`block hard-card p-4 hover:shadow-sm transition-colors border-l-4 ${SEV_COLOR[n.severity] || "border-zinc-300 bg-white"} hover:border-l-[#FF5F15]`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isCritical ? "bg-red-600 text-white" : "bg-zinc-100 text-zinc-700 dark:bg-white/10 dark:text-zinc-200"}`}>
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm leading-relaxed">{n.title}</div>
                      <ArrowUpRight className="w-4 h-4 text-zinc-400 shrink-0" />
                    </div>
                    <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">{n.subtitle}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                      {new Date(n.created_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
